/**
 * VideoPlayer
 *
 * Lightweight hover-activated player used inside template grids/cards. Mutes,
 * loops, and preloads for snappy previews.
 */
import React, { useEffect, useRef, useState } from 'react';

const VideoPlayer = ({ 
  videoUrl, 
  templateId, 
  isHovered,
  className = "",
  style = {}
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  
  // Initialize video when component mounts
  useEffect(() => {
    if (!videoRef.current || !videoUrl) return;
    
    console.log(`VideoPlayer mounted for template ${templateId}`);
    
    // Add event listeners
    const handleCanPlay = () => {
      console.log(`Video can play for ${templateId}`);
    };
    
    const handleError = (e) => {
      console.error(`Video error for ${templateId}:`, e);
      setError(e);
    };
    
    videoRef.current.addEventListener('canplay', handleCanPlay);
    videoRef.current.addEventListener('error', handleError);
    
    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener('canplay', handleCanPlay);
        videoRef.current.removeEventListener('error', handleError);
      }
    };
  }, [videoUrl, templateId]);
  
  // Handle hover state changes
  useEffect(() => {
    if (!videoRef.current || !videoUrl) return;
    
    if (isHovered) {
      try {
        videoRef.current.currentTime = 0;
        videoRef.current.muted = true;
        
        console.log(`Attempting to play video for ${templateId}`);
        const playPromise = videoRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log(`Video playing for ${templateId}`);
              setIsPlaying(true);
            })
            .catch(err => {
              console.error(`Video play error for ${templateId}:`, err);
              setIsPlaying(false);
              
              // Try again after a short delay
              setTimeout(() => {
                if (videoRef.current && isHovered) {
                  videoRef.current.play()
                    .then(() => setIsPlaying(true))
                    .catch(() => setIsPlaying(false));
                }
              }, 100);
            });
        }
      } catch (err) {
        console.error(`Exception playing video for ${templateId}:`, err);
      }
    } else if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isHovered, videoUrl, templateId]);
  
  if (!videoUrl) {
    return null;
  }
  
  return (
    <div className={className} style={style}>
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full h-full object-cover"
        muted
        playsInline
        loop
        preload="auto"
        crossOrigin="anonymous"
      />
    </div>
  );
};

export default VideoPlayer; 